import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import { getThemeColors, getComponentColors, Theme } from "../../theme/colors";
import RdmTable, { RdmConfigRow, RdmTableStyles } from "./components/RdmTable";
import ConfigDetails, {
  ConfigDetailsData,
  ConfigDetailsStyles,
} from "./components/ConfigDetails";
import SaveRdmModal from "./components/SaveRdmModal";
import { useNavigate } from "react-router-dom";

interface RdmMainProps {
  username: string;
  theme: Theme;
  devNumber: number;
}

export const RdmMain: React.FC<RdmMainProps> = ({
  username,
  theme,
  devNumber,
}) => {
  const colors = getThemeColors(username, theme);
  const componentColors = getComponentColors("table", theme);
  const sidebarColors = getComponentColors("sidebar", theme);

  const buttonHoverStyle: React.CSSProperties = {
    backgroundColor: sidebarColors.buttonHover || colors.buttonHover,
  };

  const [configs, setConfigs] = useState<RdmConfigRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [doubleClickedId, setDoubleClickedId] = useState<number | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [configDetails, setConfigDetails] = useState<ConfigDetailsData | null>(
    null
  );
  const [editedConfigDetails, setEditedConfigDetails] =
    useState<ConfigDetailsData | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [currentConfig, setCurrentConfig] = useState<ConfigDetailsData | null>(
    null
  );
  const [currentConfigLoading, setCurrentConfigLoading] =
    useState<boolean>(true);

  const navigate = useNavigate();
  const createEmptyConfig = (): ConfigDetailsData => {
    return {
      id: 0, // New config, no ID yet
      promptbox: "",
      sql_promptbox: "",
      specific_definitions: "",
      general_definitions: "",
      other_definitions: "",
      nr_defs_used: 5,
      config_name: "New Configuration",
      created_at: new Date().toISOString(),
      db_schema: "",
      db_tables: "",
      sample_rows_in_table_info: 3,
      max_nr_rows_from_sql_query: 50,
      nr_samples_used: 3,
      db_database: "",
    };
  };

  const handleStartWithEmptyConfig = () => {
    // Reset states and create empty config
    setDoubleClickedId(-1); // Use -1 to indicate empty config mode
    const emptyConfig = createEmptyConfig();
    setConfigDetails(emptyConfig);
    setEditedConfigDetails(emptyConfig);
    setDetailsError(null);
    setDetailsLoading(false);
    setSelectedId(null);
    setHasUnsavedChanges(true); // Mark as unsaved since it's a new config
  };

  const handleSaveConfig = (savedConfig: ConfigDetailsData) => {
    // Update both the original and edited config details with the saved configuration data
    setConfigDetails(savedConfig);
    setEditedConfigDetails(savedConfig);
    setHasUnsavedChanges(false);
    navigate(`/user_manager`);
  };

  const handleUnsavedChangesClick = () => {
    if (hasUnsavedChanges && editedConfigDetails) {
      setShowSaveModal(true);
    }
  };

  const handleEditedConfigChange = (editedConfig: ConfigDetailsData) => {
    setEditedConfigDetails(editedConfig);
  };

  const handleLoadConfigs = async () => {
    // Reset any loaded config details and selection so the list view is shown
    setDoubleClickedId(null);
    setConfigDetails(null);
    setEditedConfigDetails(null);
    setDetailsError(null);
    setDetailsLoading(false);
    setSelectedId(null);
    setHasUnsavedChanges(false);
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${config.api.baseUrl}/chat/rdm`, {
        username,
      });
      setConfigs(response.data?.rdm ?? []);
    } catch (err: unknown) {
      const message =
        typeof err === "object" && err !== null && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error || "Failed to load configs"
          : "Failed to load configs";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: "transparent",
    color: colors.text,
    border: `1px solid ${colors.border}`,
    padding: "10px 20px",
    fontSize: "16px",
    cursor: "pointer",
    margin: "10px",
    borderRadius: "4px",
  };

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: componentColors.background || colors.background,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: colors.optionsBg,
    color: colors.text,
    padding: "12px 16px",
    textAlign: "left",
    fontWeight: "bold",
    borderBottom: `1px solid ${colors.border}`,
    userSelect: "none",
    position: "relative",
  };

  const cellStyle: React.CSSProperties = {
    padding: "12px 16px",
    borderBottom: `1px solid ${colors.border}`,
  };

  const rowStyle: React.CSSProperties = {
    cursor: "pointer",
  };

  const lastHeaderStyle: React.CSSProperties = {
    ...headerStyle,
    textAlign: "center",
    width: "135px",
  };

  const lastCellStyle: React.CSSProperties = {
    ...cellStyle,
    textAlign: "center",
    whiteSpace: "nowrap",
    width: "135px",
  };

  const filterInputStyle: React.CSSProperties = {
    width: "100%",
    padding: "4px 8px",
    border: `1px solid ${colors.border}`,
    borderRadius: "4px",
    backgroundColor: colors.background,
    color: colors.text,
    fontSize: "12px",
  };

  const filterRowStyle: React.CSSProperties = {
    backgroundColor: colors.convoStarterBg,
  };

  const tableStyles: RdmTableStyles = {
    tableStyle,
    headerStyle,
    lastHeaderStyle,
    cellStyle,
    lastCellStyle,
    filterRowStyle,
    filterInputStyle,
    rowStyle,
    rowHoverColor: colors.convoStarterHover,
  };

  const detailsStyles: ConfigDetailsStyles = {
    container: {
      marginTop: "20px",
      padding: "16px",
      backgroundColor: componentColors.background || colors.background,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      borderRadius: "8px",
    },
    tabBar: {
      display: "flex",
      gap: "8px",
      marginBottom: "12px",
    },
    tabButton: {
      backgroundColor: "transparent",
      color: colors.text,
      border: `1px solid ${colors.border}`,
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
    },
    tabButtonActive: {
      backgroundColor: colors.optionsBg,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
    },
    section: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    row: {
      display: "grid",
      gridTemplateColumns: "200px 1fr",
      gap: "12px",
      padding: "8px 0",
      borderBottom: `1px solid ${colors.border}`,
    },
    label: {
      fontWeight: 600,
    },
    value: {},
  };

  // Fetch current config on component mount and load it automatically
  useEffect(() => {
    const fetchCurrentConfig = async () => {
      setCurrentConfigLoading(true);
      try {
        const response = await axios.post(
          `${config.api.baseUrl}/chat/current_rdm_config`,
          {
            username,
          }
        );
        const currentConfigData = response.data?.config ?? null;
        setCurrentConfig(currentConfigData);

        // Automatically load the current config if it exists
        if (currentConfigData && currentConfigData.id) {
          setDoubleClickedId(currentConfigData.id);
        }
      } catch (err: unknown) {
        // Silently handle error - current config might not exist
        setCurrentConfig(null);
      } finally {
        setCurrentConfigLoading(false);
      }
    };

    fetchCurrentConfig();
  }, [username]);

  useEffect(() => {
    const fetchDetails = async (configId: number) => {
      setDetailsLoading(true);
      setDetailsError(null);
      setConfigDetails(null);
      try {
        const response = await axios.post(
          `${config.api.baseUrl}/chat/rdm_config`,
          {
            username,
            config_id: configId,
          }
        );
        const loadedConfig = response.data?.config ?? null;
        setConfigDetails(loadedConfig);
        setEditedConfigDetails(loadedConfig);
        setHasUnsavedChanges(false);
      } catch (err: unknown) {
        const message =
          typeof err === "object" && err !== null && "response" in err
            ? (err as { response?: { data?: { error?: string } } }).response
                ?.data?.error || "Failed to load config details"
            : "Failed to load config details";
        setDetailsError(message);
      } finally {
        setDetailsLoading(false);
      }
    };

    if (doubleClickedId !== null && doubleClickedId !== -1) {
      fetchDetails(doubleClickedId);
    }
    // No need to fetch for empty config (doubleClickedId === -1)
  }, [doubleClickedId, username]);

  return (
    <div
      style={{
        padding: "20px",
        height: "calc(100vh - 48px)",
        backgroundColor: componentColors.background || colors.background,
        color: colors.text,
        overflowY: "auto",
      }}
    >
      <h1
        style={{
          marginBottom: "20px",
          color: colors.text,
          fontSize: "24px",
          fontWeight: "bold",
        }}
      >
        RDM
        {currentConfigLoading && (
          <span
            style={{
              color: colors.text,
              opacity: 0.7,
              fontWeight: "normal",
              marginLeft: "8px",
              fontSize: "16px",
            }}
          >
            (Loading current config...)
          </span>
        )}
        {!currentConfigLoading && currentConfig && (
          <span
            style={{
              color: colors.text,
              opacity: 0.8,
              fontWeight: "normal",
              marginLeft: "8px",
              fontSize: "16px",
            }}
          >
            (Currently assigned ID: {currentConfig.id})
          </span>
        )}
        {!currentConfigLoading && !currentConfig && (
          <span
            style={{
              color: "#ff6b35",
              opacity: 0.8,
              fontWeight: "normal",
              marginLeft: "8px",
              fontSize: "16px",
            }}
          >
            (No current config assigned)
          </span>
        )}
      </h1>
      <button
        style={buttonStyle}
        onClick={handleStartWithEmptyConfig}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            buttonHoverStyle.backgroundColor || "";
        }}
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "transparent")
        }
      >
        Start With Empty Config
      </button>
      <button
        style={buttonStyle}
        onClick={handleLoadConfigs}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor =
            buttonHoverStyle.backgroundColor || "";
        }}
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "transparent")
        }
      >
        Load Existing Config
      </button>
      {configDetails && (
        <span
          style={{
            marginLeft: "12px",
            color:
              configDetails.id === 0 || hasUnsavedChanges
                ? "#ff6b35"
                : colors.text,
            opacity: configDetails.id === 0 || hasUnsavedChanges ? 1 : 0.8,
            verticalAlign: "middle",
            fontWeight:
              configDetails.id === 0 || hasUnsavedChanges ? "bold" : "normal",
            cursor: hasUnsavedChanges ? "pointer" : "default",
            textDecoration: hasUnsavedChanges ? "underline" : "none",
          }}
          onClick={handleUnsavedChangesClick}
          title={hasUnsavedChanges ? "Click to save configuration" : ""}
        >
          {configDetails.id === 0
            ? "New Configuration"
            : hasUnsavedChanges
            ? "Unsaved RDM changes"
            : `Loaded Config ID: ${configDetails.id}`}
        </span>
      )}

      {isLoading && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div>Loading configs...</div>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            marginTop: "20px",
          }}
        >
          Error: {error}
        </div>
      )}

      {!isLoading &&
        !error &&
        configs.length > 0 &&
        doubleClickedId === null && (
          <RdmTable
            data={configs}
            styles={tableStyles}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onRowDoubleClick={(row) => {
              setSelectedId(row.id);
              setDoubleClickedId(row.id);
            }}
          />
        )}

      {doubleClickedId !== null && (
        <>
          {detailsLoading && (
            <div style={{ marginTop: "20px" }}>Loading configuration...</div>
          )}
          {detailsError && (
            <div
              style={{
                marginTop: "20px",
                padding: "12px",
                backgroundColor: "#f8d7da",
                color: "#721c24",
                border: "1px solid #f5c6cb",
                borderRadius: "4px",
              }}
            >
              Error: {detailsError}
            </div>
          )}
          {!detailsLoading && !detailsError && configDetails && (
            <ConfigDetails
              configData={configDetails}
              username={username}
              theme={theme}
              styles={detailsStyles}
              onConfigChange={setHasUnsavedChanges}
              onEditedConfigChange={handleEditedConfigChange}
              devNumber={devNumber}
              labels={{
                promptbox: "Conversation Agent",
                sql_promptbox: "SQL Agent",
                specific_definitions: "Definitions: Domain",
                general_definitions: "SQL Instructions",
                other_definitions: "Definitions: Client",
                nr_defs_used: "Max number of definitions to use",
                config_name: "Config Name",
                created_at: "Created At",
                db_schema: "DB Schema",
                db_tables: "DB Tables",
                sample_rows_in_table_info:
                  "Number of samples to use in table info",
                max_nr_rows_from_sql_query:
                  "Max number of rows provided for the prompt from the SQL results",
                db_database: "Database",
                id: "ID",
              }}
            />
          )}
        </>
      )}

      <SaveRdmModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleSaveConfig}
        username={username}
        theme={theme}
        configData={editedConfigDetails}
      />
    </div>
  );
};
